import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * Toggleable chip for filter UIs. Tone communicates state.
 *
 *   <Chip icon={<Car size={14} />} label="Car accident" count={1284} />
 *   <Chip label="Free consult" selected />
 */

export type ChipTone = 'default' | 'ember' | 'sage';

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    label: string;
    icon?: ReactNode;
    count?: number;
    selected?: boolean;
    tone?: ChipTone;
}

const TONE_CLASSES: Record<ChipTone, { idle: string; on: string }> = {
    default: {
        idle: 'bg-card text-ink-soft border-rule hover:border-ink-soft',
        on: 'bg-ink text-paper border-ink',
    },
    ember: {
        idle: 'bg-ember-tint/40 text-ember-deep border-ember-tint hover:bg-ember-tint',
        on: 'bg-ember text-paper border-ember',
    },
    sage: {
        idle: 'bg-sage-tint/40 text-sage border-sage-tint hover:bg-sage-tint',
        on: 'bg-sage text-paper border-sage',
    },
};

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
    {
        label,
        icon,
        count,
        selected = false,
        tone = 'default',
        type = 'button',
        className,
        ...rest
    },
    ref,
) {
    const tones = TONE_CLASSES[tone];
    const cls = [
        'inline-flex items-center gap-1.5 h-8 px-3 text-[13px] font-medium',
        'rounded-full border transition-colors cursor-pointer',
        selected ? tones.on : tones.idle,
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <button
            ref={ref}
            type={type}
            className={cls}
            aria-pressed={selected}
            {...rest}
        >
            {icon}
            <span>{label}</span>
            {count !== undefined && (
                <span className="text-[11px] opacity-75 ml-0.5 font-mono">
                    {count.toLocaleString()}
                </span>
            )}
        </button>
    );
});

export default Chip;
